import React from 'react';

import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { CouponFilledIcon, MinusCircleFilledIcon, PlusCircleFilledIcon } from '@icons';

import { useModals } from '@shared/contexts';

import ticketPlaceholder from '@assets/images/promotions/ticket_placeholder.webp';

import { useMutationBuyShopItem } from '@hooks/api/shop';
import useGetPointsIcon from '@hooks/use-get-points-icon';

import { ITEM_TYPE, PromotionTicketType } from '@types';

import { Button } from '@components/button';
import { Modal } from '@components/modal';

import { config } from '@/config.ts';

import { ModalName } from '../types';
import * as Styled from './promotion-buy-ticket-modal.styled';

interface ModalDataType {
	ticket: PromotionTicketType;
}

const isNgu3 = config.BRAND === 'ngu3';
const isRockets = config.BRAND === 'rockets';
const isBlitz = config.BRAND === 'blitz';
const isSk2 = config.BRAND === 'sk2';

const isExternalBrand = isNgu3 || isRockets || isBlitz || isSk2;

const PromotionBuyTicketModal = () => {
	const { t } = useTranslation('general');

	const { isModalOpen, closeModal, getModalData } = useModals();
	const [ticketAmount, setTicketAmount] = React.useState<number>(1);
	const PointsIcon = useGetPointsIcon();
	const { buyItem, isLoading } = useMutationBuyShopItem();

	const modalData = getModalData<ModalDataType>(ModalName.PROMOTION_TICKETS);

	React.useEffect(() => {
		setTicketAmount(1);
	}, [isModalOpen]);

	const handleTicketDecrease = () => {
		if (ticketAmount === 1) return;

		setTicketAmount(ticketAmount - 1);
	};

	const handleTicketIncrease = () => {
		if (ticketAmount === modalData?.ticket.limit_24h) return;

		setTicketAmount(ticketAmount + 1);
	};

	const handleBuyShopItem = () => {
		if (modalData) {
			try {
				buyItem(ITEM_TYPE.LOTTERY, modalData?.ticket.id, ticketAmount);
			} catch (err) {
				toast.error('Something wrong');
				console.error(err);
			}
		}
	};

	return (
		<Modal
			title={modalData?.ticket.name}
			titlePosition='center'
			isOpen={isModalOpen(ModalName.PROMOTION_TICKETS)}
			closeModal={closeModal}
		>
			<Styled.Wrapper>
				<Styled.ImageWrapper>
					<Styled.Image src={modalData?.ticket.image ? modalData.ticket.image : ticketPlaceholder} />
				</Styled.ImageWrapper>
				<Styled.ChipRow>
					<Styled.Chip>
						{modalData?.ticket.price + ' '}
						{isExternalBrand ? t('points_p') : t('pmPointsPlural')}
					</Styled.Chip>
				</Styled.ChipRow>
				<Styled.Description>{modalData?.ticket.description}</Styled.Description>
				<Styled.FooterRow>
					<Styled.FooterLeft>
						<Styled.AmountPickerWrapper>
							<Styled.AmountButton $isDisabled={ticketAmount === 1} onClick={handleTicketDecrease}>
								<MinusCircleFilledIcon />
							</Styled.AmountButton>
							<Styled.TicketAmount>
								<Styled.TicketIconWrapper>
									<CouponFilledIcon />
								</Styled.TicketIconWrapper>
								{ticketAmount}
							</Styled.TicketAmount>
							<Styled.AmountButton
								$isDisabled={ticketAmount === modalData?.ticket.limit_24h}
								onClick={handleTicketIncrease}
							>
								<PlusCircleFilledIcon />
							</Styled.AmountButton>
						</Styled.AmountPickerWrapper>
						<Styled.TicketCost>
							{modalData?.ticket.price && ticketAmount * modalData?.ticket.price}

							<Styled.PmPointsIcon>{PointsIcon}</Styled.PmPointsIcon>
						</Styled.TicketCost>
					</Styled.FooterLeft>
					<Styled.FooterRight>
						<Button onClick={closeModal} color='dark'>
							{t('close')}
						</Button>
						<Button onClick={handleBuyShopItem} disabled={isLoading} color='primary'>
							{t('buy')}
						</Button>
					</Styled.FooterRight>
				</Styled.FooterRow>
			</Styled.Wrapper>
		</Modal>
	);
};

export default PromotionBuyTicketModal;
